import React, { useContext, useRef, useState } from "react";
import Toy from "./Toy";
import { easeInOut, motion, useMotionValueEvent, useTransform } from "motion/react";
import { ScrollAnimContext } from "@/contexts/ScrollAnimContext";
import Cursor from "./Cursor";

const blockLabels = [
  ["array[]", "if (...) {}", "nlp", "map", "const"],
  ["push", "[>>+<<-]", "() => {}", "typescript", "filter"],
  ["for (let i...)", "css", "__proto__", "reduce", "{}"],
  ["bsearch", "tensorflow", "<>", "async", "O(n)"],
  ["()", ".mjs", "var", "([A-Z])w+\\g", "break"],
];

/**
 * Calculates the coordinates of target toys
 * @param targets Indexes of target toys
 * @param toyRefs References to all toy elements
 * @returns Array of coordinates for each target toy
 */
const calcCoordinates = (targets: number[], toyRefs: React.RefObject<HTMLDivElement[]>) => {
  return targets.map((target) => {
    const el = toyRefs.current[target];
    if (!el) return { x: 0, y: 0 };

    const rect = el.getBoundingClientRect();

    return {
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    };
  });
};

export default function ProgrammerPlayground() {
  // States
  const [cursorX, setCursorX] = useState(window.innerWidth / 3);
  const [cursorY, setCursorY] = useState(window.innerHeight / 1.5);

  // Refs
  const toyRefs = useRef<HTMLDivElement[]>([]);
  const targets = [0, 3, 19]; // Toys we want to target (in order)

  // Animations
  const scrollYProgress = useContext(ScrollAnimContext);
  const opacity = useTransform(scrollYProgress, [0, 0.11, 0.92, 1], [0, 1, 1, 0], { ease: easeInOut });
  const cursorOpacity = useTransform(scrollYProgress, [0.11, 0.19], [0, 1], { ease: easeInOut });
  const cursorMovementAnim = useTransform(scrollYProgress, [0.204, 0.87], [0, 1]);
  const cursorMoveOutAnim = useTransform(scrollYProgress, [0.87, 0.96], [0, 1]);

  /**
   * Move the cursor from one toy to another based on the cursorMovementAnim progress
   */
  useMotionValueEvent(cursorMovementAnim, "change", (latest) => {
    /** Some configurations */
    const duration = 1;
    const holdDuration = 0.4; // % of duration per target to hold position before moving to the next

    // Calculate progress within the entire animation
    const durationPerTarget = duration / targets.length; // Time spent on each target

    // Determine the progress for the current target
    const targetIndex = Math.floor((latest / duration) * targets.length);
    const targetProgress = (latest - durationPerTarget * targetIndex) / durationPerTarget;

    // Calculate the coordinates of the current target
    const coordinates = calcCoordinates(targets, toyRefs);

    // Plot a path to the current target with realistic movement
    const movementProgress = Math.min(targetProgress / (1 - holdDuration), 1);
    if (targetIndex < targets.length && movementProgress < 1) {
      const start =
        targetIndex === 0
          ? {
              x: window.innerWidth / 3,
              y: window.innerHeight / 1.5,
            }
          : coordinates[targetIndex - 1]!;
      const end = coordinates[targetIndex]!;

      // Add overshoot and curve to movement
      const easedProgress = easeInOut(movementProgress);
      const curveOffset = Math.sin(easedProgress * Math.PI) * 30; // Curved path offset

      const newX = start.x + (end.x - start.x) * easedProgress + curveOffset;
      const newY = start.y + (end.y - start.y) * easedProgress + curveOffset * 0.5;

      setCursorX(newX);
      setCursorY(newY);
    } else {
      // Stay at the current position (hold)
      const last = coordinates[Math.min(targetIndex, targets.length - 1)]!;
      setCursorX(last.x);
      setCursorY(last.y);
    }
  });

  /**
   * Move the cursor out of view at the end of the animation
   */
  useMotionValueEvent(cursorMoveOutAnim, "change", (latest) => {
    // Get the position of the last target toy
    const coordinates = calcCoordinates([targets.at(-1)!], toyRefs);

    // Move cursor out of view to the bottom of the screen from its current position
    const startX = coordinates[0]!.x;
    const startY = coordinates[0]!.y;
    const endY = window.innerHeight; // Move out of view

    setCursorX(startX); // Keep X the same
    setCursorY(startY + (endY - startY) * latest); // Move Y downwards by a % of latest progress
  });

  return (
    <motion.div className='h-full w-full p-4 md:p-6 lg:p-12' style={{ opacity }}>
      <div className='h-full w-full flex flex-col justify-around'>
        {blockLabels.map((row, i) => (
          <div className='flex items-start justify-around' key={i}>
            {row.map((label, j) => {
              const index = i * row.length + j;

              return (
                <Toy
                  key={index}
                  label={label}
                  style={{ x: ((j + i) % 2) * 50, y: (j - i) % 3 === 0 ? 50 : -25 }}
                  id={i + j}
                  ref={(el) => {
                    if (el) toyRefs.current[index] = el;
                  }}
                />
              );
            })}
          </div>
        ))}
      </div>
      <motion.div style={{ opacity: cursorOpacity }}>
        <Cursor className='absolute top-0 left-0' style={{ x: cursorX, y: cursorY }} />
      </motion.div>
    </motion.div>
  );
}
